import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { Observable, ReplaySubject } from 'rxjs';
import { environment } from "../../environments/environment";
import {
  AuditTimelinePage,
  AuditTimelineQuery,
} from "../model/audit.model";

@Injectable({
  providedIn: "root",
})
export class AuditService {
  private readonly httpClient = inject(HttpClient);
  readonly historyRevision = signal(0);
  private readonly presence = new Map<string, ReplaySubject<boolean>>();
  private readonly pending = new Map<number, Map<string, { entityName: string; entityId: number; result: ReplaySubject<boolean> }>>();
  private scheduled = false;

  invalidateHistory(): void {
    this.presence.clear();
    this.historyRevision.update(value => value + 1);
  }

  hasChanges(orgId: number, entityName: string, entityId: number): Observable<boolean> {
    const key = `${orgId}:${entityName}:${entityId}`;
    const cached = this.presence.get(key);
    if (cached) return cached.asObservable();
    const result = new ReplaySubject<boolean>(1); this.presence.set(key, result);
    const batch = this.pending.get(orgId) ?? new Map();
    batch.set(key, { entityName, entityId, result }); this.pending.set(orgId, batch);
    if (!this.scheduled) {
      this.scheduled = true;
      queueMicrotask(() => this.flushPresence());
    }
    return result.asObservable();
  }

  private flushPresence(): void {
    this.scheduled = false;
    const batches = [...this.pending.entries()]; this.pending.clear();
    for (const [orgId, batch] of batches) {
      const entries = [...batch.values()];
      for (let offset = 0; offset < entries.length; offset += 200) {
        const part = entries.slice(offset, offset + 200);
        this.httpClient.post<{ entityName: string; entityId: number }[]>(`${environment.baseUrl}/Audit/history-presence`,
          part.map(({ entityName, entityId }) => ({ entityName, entityId })), { headers: { 'X-OrgId': String(orgId) } }).subscribe({
          next: found => {
            const keys = new Set(found.map(e => `${e.entityName}:${e.entityId}`));
            part.forEach(e => { e.result.next(keys.has(`${e.entityName}:${e.entityId}`)); e.result.complete(); });
          },
          error: () => part.forEach(e => {
            const key = `${orgId}:${e.entityName}:${e.entityId}`;
            if (this.presence.get(key) === e.result) this.presence.delete(key);
            e.result.next(false); e.result.complete();
          }),
        });
      }
    }
  }

  getTimeline(query: AuditTimelineQuery) {
    let params = new HttpParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params = params.set(key, String(value));
      }
    });

    return this.httpClient.get<AuditTimelinePage>(
      `${environment.baseUrl}/${query.module === "Payroll" && query.entityName === "PayrollRun" ? "Payroll/Audit" : "Audit"}/timeline`,
      { params },
    );
  }
}
